/**
 * Header behavior (Lavazza):
 * 1) Mobile/tablet search — `.search-button-toggle` opens mobile search layer via mobileSearch.js
 * 2) Sticky offset — on scroll, banner height and breakpoint adjust `#lvz-header-template` /
 *    `#lvz-header-info-container` tops.
 * 3) Anchor offset — exposes the sticky header bottom as `--lvz-sticky-header-offset`,
 *    used by `scroll-padding-top` so in-page anchors are not covered by the header.
 */
const isMedium = require('../utils/isMedium');
const anchorDomUtils = require('../utils/anchorDomUtils');
const mobileSearch = require('./mobileSearch');

const MAIN_HEADER_EVENT_NS = 'lvzMainHeader';
const MOBILE_SEARCH_BAR_COLLAPSED_CLASS = 'is-mobile-search-bar-collapsed';
const STICKY_HEADER_SELECTORS = ['#lvz-top-banner', '#lvz-header-info-container', '#lvz-header-template'];

const SCROLL = {
	collapseInfoAfterPx: 10,
};

module.exports = function initHeader() {
	const $bar = $('.main-header-bar');
	const $searchToggle = $bar.find('.search-button-toggle');
	const $topBanner = $('#lvz-top-banner');
	const $headerInfo = $('#lvz-header-info-container');
	const $headerTemplate = $('#lvz-header-template');
	const $searchMobile = $('#header-search-mobile');
	const hasMobileSearchControls = $searchToggle.length && $searchMobile.length;

	let lastScrollTop = 0;
	// Scroll position at which the collapsed search bar was re-expanded from the toggle (null when not pinned).
	let searchBarReopenedAt = null;

	if (!$bar.length || !$headerTemplate.length) {
		return;
	}

	function isCompactViewport() {
		return !isMedium();
	}

	function isSearchOpen() {
		return mobileSearch.isOpen();
	}

	function isMobileSearchBarCollapsed() {
		return $('html').hasClass(MOBILE_SEARCH_BAR_COLLAPSED_CLASS);
	}

	function setMobileSearchBarCollapsed(isCollapsed) {
		if (!hasMobileSearchControls) {
			return;
		}

		const wasCollapsed = isMobileSearchBarCollapsed();

		$('html').toggleClass(MOBILE_SEARCH_BAR_COLLAPSED_CLASS, isCollapsed);
		$searchToggle.attr('aria-hidden', isCollapsed ? 'false' : 'true');

		if (wasCollapsed !== isCollapsed) {
			$(document).trigger('lavazza:headerSearchLayoutChange');
		}
	}

	function syncMobileSearchBarState(scrollTop) {
		if (!hasMobileSearchControls) {
			return;
		}

		if (!isCompactViewport()) {
			searchBarReopenedAt = null;
			setMobileSearchBarCollapsed(false);
			return;
		}

		// Keep the bar re-opened from the toggle visible: its own height change shifts scrollTop
		// (scroll anchoring), so only a real scroll down past its height collapses it again.
		if (searchBarReopenedAt !== null) {
			const tolerance = ($searchMobile.outerHeight(true) || 0) + SCROLL.collapseInfoAfterPx;

			if (scrollTop > SCROLL.collapseInfoAfterPx && scrollTop - searchBarReopenedAt <= tolerance) {
				return;
			}

			searchBarReopenedAt = null;
		}

		setMobileSearchBarCollapsed(scrollTop > SCROLL.collapseInfoAfterPx && !isSearchOpen());
	}

	/**
	 * SweetAlert2 modal/drawer open (excludes toasts, which use swal2-toast-shown).
	 * @returns {boolean}
	 */
	function isModalOpen() {
		if (!document.body || !document.body.classList) {
			return false;
		}

		return document.body.classList.contains('swal2-shown')
			&& !document.body.classList.contains('swal2-toast-shown');
	}

	function openSearch() {
		if (!hasMobileSearchControls || !isCompactViewport()) {
			return;
		}

		if (isMobileSearchBarCollapsed()) {
			searchBarReopenedAt = $(window).scrollTop();
			setMobileSearchBarCollapsed(false);
			$searchToggle.attr('aria-expanded', 'false');
			return;
		}

		$searchToggle.attr('aria-expanded', 'true');
		mobileSearch.openFromToggle();
	}

	function closeSearch() {
		if (!hasMobileSearchControls) {
			return;
		}

		$searchToggle.attr('aria-expanded', 'false');
		mobileSearch.close();
	}

	function bindNavTogglerClosesSearch() {
		$(document).off(`click.${MAIN_HEADER_EVENT_NS}NavToggle`);
		$(document).on(`click.${MAIN_HEADER_EVENT_NS}NavToggle`, '.navbar-toggler', () => {
			if (!isCompactViewport()) {
				return;
			}

			if (isSearchOpen()) {
				closeSearch();
			}
		});
	}

	function bindSearchCloseOnEscape() {
		$(document).off(`keydown.${MAIN_HEADER_EVENT_NS}`);

		$(document).on(`keydown.${MAIN_HEADER_EVENT_NS}`, e => {
			if (!isCompactViewport() || !isSearchOpen()) {
				return;
			}

			if (e.key === 'Escape' || e.keyCode === 27) {
				e.preventDefault();
				closeSearch();
			}
		});
	}

	function getStickyHeaderBottom() {
		return STICKY_HEADER_SELECTORS.reduce((maxBottom, selector) => {
			const element = document.querySelector(selector);

			if (!element) {
				return maxBottom;
			}

			const rect = element.getBoundingClientRect();

			if (rect.height < 1 || rect.bottom <= 0) {
				return maxBottom;
			}

			return Math.max(maxBottom, rect.bottom);
		}, 0);
	}

	function syncStickyHeaderOffset() {
		document.documentElement.style.setProperty('--lvz-sticky-header-offset', `${getStickyHeaderBottom()}px`);
	}

	// The browser may jump to the URL hash before the offset is known: realign once the layout is ready.
	function alignInitialHashTarget() {
		const target = anchorDomUtils.getAnchorTarget(window.location.hash, document);

		if (!target) {
			return;
		}

		syncStickyHeaderOffset();
		target.scrollIntoView({ block: 'start' });
	}

	function updateStickyHeaderOffsets() {
		const scrollTop = $(window).scrollTop();
		const goingDownSticky = scrollTop > lastScrollTop;
		const topBannerHeight = $topBanner.height() || 0;
		const modalOpen = isModalOpen();
		const desktopSearchOpen = $('html').hasClass('is-desktop-search-open');

		syncMobileSearchBarState(scrollTop);

		if (desktopSearchOpen && isMedium()) {
			$headerInfo.removeClass('opacity-0 -translate-y-full');
			$headerInfo.css('top', topBannerHeight);
			$headerTemplate.css('top', topBannerHeight + $headerInfo.height());
			lastScrollTop = scrollTop;
			syncStickyHeaderOffset();
			return;
		}

		if (goingDownSticky && scrollTop > SCROLL.collapseInfoAfterPx && !modalOpen) {
			$headerInfo.addClass('opacity-0 -translate-y-full');
			$headerInfo.css('top', topBannerHeight);
			$headerTemplate.css('top', topBannerHeight);
		} else if (isMedium() && !modalOpen) {
			$headerInfo.removeClass('opacity-0 -translate-y-full');
			$headerTemplate.css('top', topBannerHeight + $headerInfo.height());
		} else if (!modalOpen) {
			$headerTemplate.css('top', topBannerHeight);
		}

		lastScrollTop = scrollTop;
		syncStickyHeaderOffset();
	}

	function setup() {
		$searchToggle.off(`click.${MAIN_HEADER_EVENT_NS}`);

		if (!hasMobileSearchControls || !isCompactViewport()) {
			$(document).off(`keydown.${MAIN_HEADER_EVENT_NS} click.${MAIN_HEADER_EVENT_NS}NavToggle`);
			setMobileSearchBarCollapsed(false);
			if (isSearchOpen()) {
				closeSearch();
			}
			return;
		}

		bindSearchCloseOnEscape();
		bindNavTogglerClosesSearch();

		$searchToggle.on(`click.${MAIN_HEADER_EVENT_NS}`, e => {
			e.preventDefault();
			if (!isCompactViewport()) {
				return;
			}

			if (isSearchOpen()) {
				closeSearch();
			} else {
				openSearch();
			}
		});
	}

	setup();
	updateStickyHeaderOffsets();

	$(window).on('load', () => {
		updateStickyHeaderOffsets();
		alignInitialHashTarget();
	});
	$headerInfo.on(`transitionend.${MAIN_HEADER_EVENT_NS}`, syncStickyHeaderOffset);
	$(window).on(`resize.${MAIN_HEADER_EVENT_NS}`, () => {
		setup();
		updateStickyHeaderOffsets();
	});
	$(window).on('scroll', updateStickyHeaderOffsets);
};
